import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Droplets,
  Filter,
  RefreshCw,
  Cpu,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { calendarService } from '@/services';
import type { ApiCalendarEvent } from '@/types/api';
import { cn } from '@/lib/utils';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Componente para evento individual
function CalendarEventCard({ event }: { event: ApiCalendarEvent }) {
  const isLocal = event.source === 'local';
  const syncStatus = event.syncStatus;
  
  // Colores y estilos según origen
  const cardClasses = isLocal
    ? 'p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-colors'
    : 'p-3 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors';

  // Badge de estado de sincronización para locales
  const getSyncStatusBadge = () => {
    if (!isLocal || !syncStatus) return null;
    
    const statusColors = {
      synced: 'bg-green-500/10 text-green-600 border-green-500/20',
      syncing: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      error: 'bg-red-500/10 text-red-600 border-red-500/20',
      pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    };
    
    const statusLabels = {
      synced: 'Sincronizada',
      syncing: 'Sincronizando',
      error: 'Error',
      pending: 'Pendiente',
    };

    return (
      <Badge variant="outline" className={cn('text-xs', statusColors[syncStatus])}>
        {statusLabels[syncStatus]}
      </Badge>
    );
  };

  return (
    <motion.div variants={itemVariants} className={cardClasses}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm truncate">{event.scheduleName || 'Sin nombre'}</h4>
            {isLocal && (
              <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20">
                Local
              </Badge>
            )}
            {!isLocal && (
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">
                Remoto
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
            <Clock className="w-3 h-3" />
            <span>{event.startTime || '--:--'} - {event.endTime || '--:--'}</span>
            <Badge variant="outline" className="ml-1 text-xs">
              {event.duration || 0} min
            </Badge>
            {getSyncStatusBadge()}
          </div>
        </div>
        {!event.enabled && (
          <Badge variant="secondary" className="text-xs">Desactivado</Badge>
        )}
      </div>
      
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span className="truncate">{event.garden?.name || 'Sin jardín'}</span>
        </div>
        {event.zone && (
          <div className="flex items-center gap-1">
            <Droplets className="w-3 h-3" />
            <span className="truncate">{event.zone.name}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Cpu className="w-3 h-3" />
          <span className="truncate">{event.controller?.name || 'Sin controlador'} - {event.valve?.name || 'Sin válvula'}</span>
        </div>
      </div>
    </motion.div>
  );
}

// Componente para vista diaria
function DayView({ events, date }: { events: ApiCalendarEvent[]; date: Date }) {
  // Filtrar eventos del día específico
  const dayDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const dayEvents = events.filter(e => e.date === dayDateStr);
  
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const eventsByHour = hours.map(hour => {
    return dayEvents.filter(e => {
      const startHour = parseInt(e.startTime?.split(':')[0] || '0');
      const endHour = parseInt(e.endTime?.split(':')[0] || '0');
      return hour >= startHour && hour < endHour;
    });
  });

  return (
    <div className="space-y-1">
      {hours.map(hour => (
        <div key={hour} className="flex gap-2 min-h-[60px]">
          <div className="w-16 text-xs text-muted-foreground pt-1 text-right pr-2 border-r">
            {hour.toString().padStart(2, '0')}:00
          </div>
          <div className="flex-1 grid gap-1">
            {eventsByHour[hour].map((event, idx) => (
              <CalendarEventCard key={`${event.scheduleId || idx}-${idx}`} event={event} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Componente para vista semanal
function WeekView({ 
  events, 
  startDate, 
  selectedDate, 
  onSelectDate 
}: { 
  events: ApiCalendarEvent[]; 
  startDate: Date;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div className="grid grid-cols-7 gap-2">
      {days.map((day, idx) => {
        // Formatear fecha del día como YYYY-MM-DD para comparar con e.date
        const dayDateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
        const dayEvents = events.filter(e => e.date === dayDateStr);
        const isToday = day.toDateString() === new Date().toDateString();
        const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
        
        return (
          <div 
            key={idx} 
            className={`min-h-[200px] cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-2 rounded-lg' : ''}`}
            onClick={() => onSelectDate(day)}
          >
            <div className={`text-center p-2 rounded-t-lg ${isToday ? 'bg-primary text-primary-foreground' : isSelected ? 'bg-primary/80 text-primary-foreground' : 'bg-muted'}`}>
              <p className="text-xs font-medium">{dayNames[day.getDay()]}</p>
              <p className="text-lg font-bold">{day.getDate()}</p>
            </div>
            <div className="p-1 space-y-1 bg-muted/30 rounded-b-lg min-h-[150px]">
              {dayEvents.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Sin riegos</p>
              ) : (
                dayEvents.slice(0, 3).map((event, eIdx) => (
                  <div 
                    key={eIdx}
                    className="p-1.5 rounded bg-primary/20 text-xs"
                    title={`${event.scheduleName || 'Sin nombre'} - ${event.startTime || '--:--'}`}
                  >
                    <p className="font-medium truncate">{event.startTime || '--:--'}</p>
                    <p className="truncate text-muted-foreground">{event.scheduleName || 'Sin nombre'}</p>
                  </div>
                ))
              )}
              {dayEvents.length > 3 && (
                <p className="text-xs text-center text-muted-foreground">
                  +{dayEvents.length - 3} más
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Componente para vista mensual
function MonthView({ 
  events, 
  date, 
  selectedDate, 
  onSelectDate 
}: { 
  events: ApiCalendarEvent[]; 
  date: Date;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
}) {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const days = Array.from({ length: 42 }, (_, i) => {
    const dayNum = i - startDay + 1;
    if (dayNum < 1 || dayNum > totalDays) return null;
    return new Date(year, month, dayNum);
  });

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(name => (
          <div key={name} className="text-center text-xs font-medium text-muted-foreground p-2">
            {name}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          if (!day) return <div key={idx} className="h-20 bg-muted/20 rounded" />;
          
          // Formatear fecha del día como YYYY-MM-DD para comparar con e.date
          const dayDateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
          const dayEvents = events.filter(e => e.date === dayDateStr);
          const isToday = day.toDateString() === new Date().toDateString();
          const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
          
          return (
            <div 
              key={idx}
              className={`h-20 p-1 rounded border cursor-pointer transition-all hover:bg-muted/50 ${
                isSelected ? 'border-primary border-2 bg-primary/10' : 
                isToday ? 'border-primary bg-primary/5' : 'border-border'
              }`}
              onClick={() => onSelectDate(day)}
            >
              <p className={`text-xs font-medium ${isToday || isSelected ? 'text-primary' : ''}`}>
                {day.getDate()}
              </p>
              {dayEvents.length > 0 && (
                <div className="mt-1">
                  <Badge variant={isSelected ? 'default' : 'secondary'} className="text-xs">
                    {dayEvents.length} riego{dayEvents.length > 1 ? 's' : ''}
                  </Badge>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Componente para mostrar detalle del día seleccionado
function SelectedDayDetail({ events, date }: { events: ApiCalendarEvent[]; date: Date }) {
  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p>No hay riegos programados para este día</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event, idx) => (
        <motion.div
          key={event.scheduleId || idx}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.05 }}
          className="p-4 rounded-lg bg-muted/50 border hover:bg-muted/70 transition-colors"
        >
          <div className="flex items-start justify-between gap-4">
            {/* Hora y duración */}
            <div className="flex-shrink-0 text-center px-3 py-2 rounded-lg bg-primary/10">
              <p className="text-lg font-bold text-primary">{event.startTime || '--:--'}</p>
              <p className="text-xs text-muted-foreground">{event.duration || 0} min</p>
            </div>

            {/* Info principal */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold truncate">{event.scheduleName || 'Sin nombre'}</h4>
                <Badge variant={event.enabled ? 'default' : 'secondary'}>
                  {event.enabled ? 'Activo' : 'Desactivado'}
                </Badge>
              </div>
              
              {/* Detalles: Jardín, Zona, Controlador, Válvula */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-success flex-shrink-0" />
                  <div>
                    <span className="text-muted-foreground">Jardín: </span>
                    <span className="font-medium">{event.garden?.name || 'Sin jardín'}</span>
                    {event.garden?.municipality && (
                      <span className="text-muted-foreground text-xs ml-1">({event.garden.municipality})</span>
                    )}
                  </div>
                </div>
                
                {event.zone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Droplets className="w-4 h-4 text-info flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">Zona: </span>
                      <span className="font-medium">{event.zone.name}</span>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-sm">
                  <Cpu className="w-4 h-4 text-warning flex-shrink-0" />
                  <div>
                    <span className="text-muted-foreground">Controlador: </span>
                    <span className="font-medium">{event.controller?.name || 'Sin controlador'}</span>
                    {event.controller?.deviceEUI && (
                      <span className="text-muted-foreground text-xs ml-1 font-mono">({event.controller.deviceEUI.slice(-8)})</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">{event.valve?.channel || '?'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Válvula: </span>
                    <span className="font-medium">{event.valve?.name || 'Sin válvula'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hora fin */}
            <div className="flex-shrink-0 text-right">
              <p className="text-xs text-muted-foreground">Fin</p>
              <p className="font-medium">{event.endTime || '--:--'}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default function Calendar() {
  const [view, setView] = useState<'day' | 'week' | 'month'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedGarden, setSelectedGarden] = useState<string>('all');

  // Formatear fecha para la API según la vista
  // Para mes: primer día del mes (ej: 2026-01-01)
  // Para semana: primer día de la semana (lunes)
  // Para día: fecha del día actual
  const getDateForApi = (date: Date, viewType: 'day' | 'week' | 'month'): string => {
    const d = new Date(date);
    
    if (viewType === 'month') {
      // Primer día del mes
      d.setDate(1);
    } else if (viewType === 'week') {
      // Primer día de la semana (lunes)
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajustar para que lunes sea 0
      d.setDate(diff);
    }
    // Para 'day', usar la fecha tal cual
    
    // Formatear como YYYY-MM-DD
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateStr = getDateForApi(currentDate, view);

  // Obtener eventos del calendario
  const { data: calendarData, isLoading, error, refetch } = useQuery({
    queryKey: ['calendar', view, dateStr, selectedGarden],
    queryFn: async () => {
      try {
        const params: any = { view, date: dateStr };
        if (selectedGarden !== 'all') {
          params.gardenIds = selectedGarden;
        }
        const response = await calendarService.getEvents(params);
        if (!response.success) {
          console.error('[Calendar] Error response:', response.error);
          return { events: [], filters: null, view, startDate: dateStr, endDate: dateStr };
        }
        console.log('[Calendar] Data received:', response.data);
        return response.data;
      } catch (e) {
        console.error('[Calendar] Error fetching calendar:', e);
        return { events: [], filters: null, view, startDate: dateStr, endDate: dateStr };
      }
    },
    retry: false,
  });

  const events = Array.isArray(calendarData?.events) ? calendarData.events : [];
  const filters = calendarData?.filters;
  
  // Log para debugging
  console.log('[Calendar] Events loaded:', events.length, 'for view:', view, 'date:', dateStr);

  // Navegación
  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Título según la vista
  const getTitle = () => {
    if (view === 'day') {
      return currentDate.toLocaleDateString('es-ES', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long',
        year: 'numeric'
      });
    } else if (view === 'week') {
      const endDate = new Date(currentDate);
      endDate.setDate(endDate.getDate() + 6);
      return `${currentDate.getDate()} - ${endDate.getDate()} ${currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-2">
            <CalendarIcon className="w-8 h-8 text-primary" />
            Calendario de Riegos
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualiza y gestiona la programación de riegos
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* View selector */}
            <div className="flex items-center gap-2">
              <Button
                variant={view === 'day' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('day')}
              >
                Día
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('week')}
              >
                Semana
              </Button>
              <Button
                variant={view === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('month')}
              >
                Mes
              </Button>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigate('prev')}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Hoy
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('next')}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Filter */}
            {filters?.gardens && filters.gardens.length > 0 && (
              <Select value={selectedGarden} onValueChange={setSelectedGarden}>
                <SelectTrigger className="w-[200px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por jardín" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los jardines</SelectItem>
                  {filters.gardens.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Title */}
      <div className="text-center">
        <h2 className="text-xl font-display font-semibold capitalize">
          {getTitle()}
        </h2>
        <p className="text-sm text-muted-foreground">
          {events.length} riego{events.length !== 1 ? 's' : ''} programado{events.length !== 1 ? 's' : ''}
          {calendarData?.startDate && calendarData?.endDate && calendarData.startDate !== calendarData.endDate && (
            <span className="ml-2 text-xs">
              ({calendarData.startDate} - {calendarData.endDate})
            </span>
          )}
        </p>
      </div>

      {/* Calendar view */}
      <Card>
        <CardContent className="py-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Error al cargar el calendario. Por favor, intenta de nuevo.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {view === 'day' && <DayView events={events} date={currentDate} />}
              {view === 'week' && (
                <WeekView 
                  events={events} 
                  startDate={currentDate} 
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              )}
              {view === 'month' && (
                <MonthView 
                  events={events} 
                  date={currentDate}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detalle del día seleccionado (solo en vista semana/mes) */}
      {(view === 'week' || view === 'month') && selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                Programaciones del {selectedDate.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SelectedDayDetail 
                events={events.filter(e => {
                  const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                  return e.date === selectedDateStr;
                })}
                date={selectedDate}
              />
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Summary */}
      {filters && (
        <Card className="bg-muted/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Resumen de filtros disponibles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Jardines:</span>{' '}
                <span className="font-medium">{filters.gardens?.length || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Municipios:</span>{' '}
                <span className="font-medium">{filters.municipalities?.length || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Zonas:</span>{' '}
                <span className="font-medium">{filters.zones?.length || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Controladores:</span>{' '}
                <span className="font-medium">{filters.controllers?.length || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
